// navChatList.js — D0 pure helpers for the agent-mode chat nav (VsNavLower.jsx).
//
// Foundation for D1-D5. All functions here are import-pure (no JSX, no DOM, no React)
// so they are node --test-able. The component wires them up; it does not own this logic.
//
// NOTE: `formatAge` is COPIED from VsNavLower.jsx (§6, originally from studio.js) so it can
// take an injectable `now` and be unit-tested. The original in VsNavLower.jsx is left intact;
// the integrator will switch the call site over. We do NOT revive the dead `buildChatList`.

// ── §6 Age format: Xs/Xm/Xh/Xd/Xw (copied from VsNavLower.jsx, now-injectable) ──
export function formatAge(ts, now = Date.now()) {
  const diff = now - ts
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d`
  const w = Math.floor(d / 7)
  return `${w}w`
}

// Pending placeholder id — a transient row shown the instant "New chat" is sent, before
// the server returns the real chat id. swapPendingId removes it when the real id arrives.
export const PENDING_CHAT_ID = '__pending__'

// Normalize a raw chats payload ([{id, title, last_active, creator, visibility, shared_user_ids}])
// into the nav's shape. Tolerates already-normalized rows ({id, title, lastActive}) and missing/odd
// values. creator ({id, name, avatar}) is carried through verbatim; nil/absent → null (legacy rows,
// non-human sessions). It drives the company-wide creator filter + per-row author label.
//
// Chat privacy 3e: carry `visibility` ('private'|'shared'|'company', default 'company' for a row that
// predates the field) so the nav can render the lock/people badge, and `sharedUserIds` (the manager-
// only prefill for the modal person-picker; [] when absent). Without this the fixed-shape map DROPPED
// both fields (they arrive from the server but never reached the row).
export function normalizeChats(chats) {
  if (!Array.isArray(chats)) return []
  return chats.map((c) => ({
    id: String(c.id),
    title: typeof c.title === 'string' ? c.title : (c.title == null ? '' : String(c.title)),
    lastActive: c.lastActive != null ? c.lastActive : (c.last_active != null ? c.last_active : 0),
    creator: c.creator != null ? c.creator : null,
    visibility: normalizeVisibility(c.visibility),
    sharedUserIds: normalizeSharedIds(c.sharedUserIds != null ? c.sharedUserIds : c.shared_user_ids),
  }))
}

// A row's visibility, defaulting to 'company' for any legacy/absent/unknown value (the safe default —
// 'company' is today's observed behavior and shows no badge, so an unknown value never mis-flags a
// chat as private/shared in the nav).
export function normalizeVisibility(v) {
  return v === 'private' || v === 'shared' || v === 'company' ? v : 'company'
}

// The manager-only share-id prefill: an array of ids (coerced to a plain array), or [] when the
// server omitted it (a non-manager viewer). Never null, so callers can spread it without a guard.
export function normalizeSharedIds(ids) {
  return Array.isArray(ids) ? ids.slice() : []
}

// Case-insensitive substring filter on title. Empty/blank query → all chats.
export function filterChatsByTitle(chats, q) {
  const list = Array.isArray(chats) ? chats : []
  const needle = (q || '').trim().toLowerCase()
  if (!needle) return list
  return list.filter((c) => String(c.title || '').toLowerCase().includes(needle))
}

// Filter chats to a single creator (author) by their id. null/'' (undefined too) = no filter
// → all chats. Rows with no creator are dropped when a creator IS selected. This is the pure
// core of the company-wide nav's creator filter; the filtering is entirely client-side because
// the payload already contains every human chat in the company.
export function filterChatsByCreator(chats, creatorId) {
  const list = Array.isArray(chats) ? chats : []
  if (creatorId == null || creatorId === '') return list
  const target = String(creatorId)
  return list.filter((c) => c.creator != null && c.creator.id != null && String(c.creator.id) === target)
}

// Distinct creators present in a chat list, first-seen order. Each entry is the raw creator
// object ({id, name, avatar}); rows without a creator are skipped. Used to decide whether to
// show the creator filter (>1 distinct author) and to populate its options.
export function distinctCreators(chats) {
  const list = Array.isArray(chats) ? chats : []
  const seen = new Map()
  for (const c of list) {
    const cr = c && c.creator
    if (cr && cr.id != null && !seen.has(String(cr.id))) {
      seen.set(String(cr.id), cr)
    }
  }
  return Array.from(seen.values())
}

// Order chats for the nav PURELY by lastActive descending (recency only) — there is NO
// active-pin. Selecting (clicking) a chat must not move it; a chat rises to the top only
// when a message is SENT to it (which bumps its lastActive). Each row carries an `age`
// string (formatAge); the active row's age is "" (we don't show an age on the open chat).
export function orderChatsForNav(chats, activeId, now = Date.now()) {
  const list = Array.isArray(chats) ? chats.slice() : []
  const active = activeId != null ? String(activeId) : null
  list.sort((a, b) => (b.lastActive || 0) - (a.lastActive || 0)) // recency only — no pin
  return list.map((c) => ({
    ...c,
    id: String(c.id),
    age: String(c.id) === active ? '' : formatAge(c.lastActive || 0, now),
  }))
}

// Build the full view-model for the nav chat list: which UI state to show + the rows.
// state: 'loading' | 'error' | 'empty' | 'list'.
export function chatListView({ chats, activeId, search, creatorId, loading, error, now = Date.now() }) {
  if (loading) {
    return { rows: [], state: 'loading', emptyLabel: '' }
  }
  if (error) {
    return { rows: [], state: 'error', emptyLabel: 'Couldn’t load chats. Retry.' }
  }
  // Company-wide list: apply the creator filter FIRST (client-side; the payload already holds
  // every author's chats), then the title-substring filter. Both no-op when unset.
  const byCreator = filterChatsByCreator(normalizeChats(chats), creatorId)
  const filtered = filterChatsByTitle(byCreator, search)
  if (filtered.length === 0) {
    const searching = (search || '').trim().length > 0
    return {
      rows: [],
      state: 'empty',
      emptyLabel: searching ? 'No matching chats.' : 'No chats yet.',
    }
  }
  return {
    rows: orderChatsForNav(filtered, activeId, now),
    state: 'list',
    emptyLabel: '',
  }
}

// QA 2026-07-23 (lock-icon bug A) — the nav-row badge is about OWNERSHIP, not raw visibility.
// The old rule (lock when visibility==='private') put a lock on the viewer's OWN private chats,
// which read as "someone else owns this". New semantics (product owner):
//   - the viewer's own chat            → NO badge, whatever its visibility (it's yours; the kebab
//                                        and modal already carry the sharing affordance)
//   - someone else's chat you can see  → 'people' (it reached your list because it's shared with
//                                        you or company-visible — the server list is fail-closed,
//                                        so a row from another creator is by definition visible)
//   - unattributable row               → NO badge (legacy/creator-less rows, or the viewer block
//                                        hasn't loaded yet — never mis-flag, matches pre-privacy UI)
//   - platform-admin viewer (2026-09-04) → NO badge on anyone's chat: the admin list is the FULL
//                                        company list, so "shared with you" would be a lie on most
//                                        rows. Admin rows carry a creator label instead (chatRowCreatorLabel).
// Pure so it is node --test-able; VsNavLower calls this per row.
export function chatRowBadge(thread, viewer) {
  if (!thread || !viewer || viewer.id == null) return null
  if (viewer.admin === true) return null
  const creator = thread.creator
  if (!creator || creator.id == null) return null
  return String(creator.id) === String(viewer.id) ? null : 'people'
}

// The small muted "whose chat is this" label on a nav row. Shown when the company is multi-user
// (teammates: first name, as before) OR the viewer is a platform admin (2026-09-04: admins see every
// chat in the company, so every row that isn't their own must say who created it; falls back to the
// admin-only creator email when the user has no name). '' when there is nothing useful to show or the
// row is the viewer's own.
export function chatRowCreatorLabel(thread, viewer, multiUser) {
  if (!thread || !thread.creator) return ''
  const creator = thread.creator
  const isAdmin = !!(viewer && viewer.admin === true)
  if (!isAdmin && !multiUser) return ''
  if (viewer && viewer.id != null && creator.id != null && String(creator.id) === String(viewer.id)) return ''
  const name = creator.name != null ? String(creator.name).trim() : ''
  if (name) return isAdmin ? name : name.split(' ')[0]
  if (isAdmin && creator.email) return String(creator.email)
  return ''
}

// §7 new-chat event payload — normalizes the created chat's id to a string.
export function chatCreatedEvent(id, title) {
  return { id: String(id), title: typeof title === 'string' ? title : (title == null ? '' : String(title)) }
}

// Prepend the transient pending placeholder row to the chat list.
export function withPendingPlaceholder(chats) {
  const list = Array.isArray(chats) ? chats : []
  return [{ id: PENDING_CHAT_ID, title: '', lastActive: Date.now() }, ...list]
}

// Remove the pending placeholder row once the real chat id is known. (The caller adds
// the real row separately, e.g. via its own fetch/refresh; this just drops __pending__.)
export function swapPendingId(chats, realId) {
  void realId
  const list = Array.isArray(chats) ? chats : []
  return list.filter((c) => String(c.id) !== PENDING_CHAT_ID)
}

// hotfix v3.28.5 — drop one chat by id (optimistic delete). Pure: returns a NEW array with the
// row removed; a non-array input yields []. String-compares ids so it matches whether the caller
// holds a numeric server id or the normalized string form. No-op if the id isn't present.
export function removeChatById(chats, id) {
  const list = Array.isArray(chats) ? chats : []
  const target = String(id)
  return list.filter((c) => String(c.id) !== target)
}

// D7: read/write the active chat id in the URL query string.
// readChatIdFromSearch('?chat=42') === '42'; missing → null.
export function readChatIdFromSearch(search) {
  if (!search) return null
  const s = search.charAt(0) === '?' ? search.slice(1) : search
  const params = new URLSearchParams(s)
  const v = params.get('chat')
  return v != null && v !== '' ? v : null
}

// writeChatIdToSearch('?foo=1', 42) → '?foo=1&chat=42'; null id removes the param.
export function writeChatIdToSearch(search, id) {
  const raw = search && search.charAt(0) === '?' ? search.slice(1) : (search || '')
  const params = new URLSearchParams(raw)
  if (id == null || id === '') {
    params.delete('chat')
  } else {
    params.set('chat', String(id))
  }
  const str = params.toString()
  return str ? `?${str}` : ''
}
